import { memo } from "react";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { Header } from "./Header";
import type { Screen } from "../types";

interface AboutPageProps {
  onBack: () => void;
  onNavigate?: (screen: Screen) => void;
}

export const AboutPage = memo(function AboutPage({
  onBack,
  onNavigate,
}: AboutPageProps) {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-slate-50 gradient-mesh">
      <Header onNavigate={onNavigate} showNav={false} />

      <main className="max-w-md mx-auto w-full px-4 py-6">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-4 p-2 hover:bg-white/80 rounded-xl transition-smooth inline-flex items-center gap-2"
          aria-label="Back"
        >
          <ArrowLeft
            className={`w-5 h-5 text-slate-600 ${
              language === "ar" ? "rotate-180" : ""
            }`}
          />
          <span className="text-slate-600 font-medium">{t("common.back")}</span>
        </button>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-lg p-6 space-y-6">
          <h1 className="text-slate-900 font-semibold text-xl mb-6">
            {t("about.title")}
          </h1>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">
              {t("about.whatIs")}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {t("about.description")}
            </p>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">
              {t("about.howItWorks")}
            </h2>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">1</span>
                </div>
                <p className="text-slate-600 leading-relaxed flex-1">
                  {t("about.step1")}
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">2</span>
                </div>
                <p className="text-slate-600 leading-relaxed flex-1">
                  {t("about.step2")}
                </p>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">3</span>
                </div>
                <p className="text-slate-600 leading-relaxed flex-1">
                  {t("about.step3")}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900">
              {t("about.points")}
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-slate-700 font-medium">
                  {t("about.pointsExact")}
                </span>
                <span className="text-primary font-bold">
                  3 {t("about.points")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-700 font-medium">
                  {t("about.pointsResult")}
                </span>
                <span className="text-primary font-bold">
                  1 {t("about.point")}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-700 font-medium">
                  {t("about.pointsWrong")}
                </span>
                <span className="text-slate-500 font-medium">
                  0 {t("about.points")}
                </span>
              </div>
            </div>
          </div>

          {/* Developer Credit */}
          <div className="border-t border-slate-200 pt-6 mt-8">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 text-center space-y-3">
              <p className="text-sm text-slate-600">
                {t("about.developedBy")}{" "}
                <a
                  href="https://rekcode.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-dark font-bold transition-smooth"
                >
                  rekcode.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
});
